import {
  BUILDINGS,
  BUILDING_IDS,
  RESOURCES,
  WORLD,
  buildBlocker,
  buildCost,
  buildTime,
  canAfford,
  farmCapacity,
  formatDuration,
  popUsed,
  rates,
  resourcesAt,
  storageCapacity,
  type BuildingId,
  type Cost,
  type Levels,
} from "../shared/game.ts";
import {
  UNITS,
  UNIT_IDS,
  carryCapacity,
  cleanUnits,
  distance,
  recruitTime,
  totalUnits,
  travelTime,
  unitCost,
  unitPop,
  type UnitId,
  type Units,
} from "../shared/units.ts";

// ---------- Typen der API ----------

interface QueueItem { id: number; building: BuildingId; level: number; startsAt: number; endsAt: number }
interface RecruitItem { id: number; unit: UnitId; count: number; delivered: number; per: number; startsAt: number; endsAt: number }
interface VillageRef { id: number; name: string; x: number; y: number }
interface Movement {
  id?: number; kind: "attack" | "support" | "return" | "incoming" | "support-in";
  startsAt: number; endsAt: number; cancelUntil?: number;
  target?: VillageRef; units?: Units; loot?: Cost;
}
interface SupportEntry { id: number; village: VillageRef & { player: string | null }; units: Units }
interface RankRow {
  rank: number; id: number; name: string; points: number; villages: number; killsAtt: number; killsDef: number;
  tribeTag: string | null; tribeColor: string | null;
}
interface TribeRankRow { rank: number; id: number; name: string; tag: string; color: string; points: number; villages: number; members: number; killsAtt: number; killsDef: number }
interface TribeOverview {
  tribe: { id: number; name: string; tag: string; color: string; description: string; createdAt: number } | null;
  role?: "leader" | "member";
  members?: { id: number; name: string; role: string; points: number; villages: number; rank: number }[];
  invites?: { id: number; name: string }[];
  messages?: { id: number; text: string; createdAt: number; author: string | null }[];
  myInvites: { id: number; name: string; tag: string; color: string }[];
  colors: string[];
}
interface Village {
  id: number; name: string; x: number; y: number;
  resources: Cost; resAt: number; levels: Levels; loyalty: number; villagesOwned: number;
  units: Units; unitsAway: Units; points: number;
  supportHere: SupportEntry[]; supportOut: SupportEntry[];
  queue: QueueItem[]; recruitQueue: RecruitItem[]; movements: Movement[];
  serverTime: number; unreadReports: number; tribeBadge: number;
}
interface Me { announcement: string; startAt: number | null; user: { id: number; name: string; tribeId: number | null; isAdmin: boolean }; villages: VillageRef[]; world: { speed: number; mapSize: number } }
interface MapVillage {
  id: number; name: string; x: number; y: number; ownerId: number | null; ownerName: string | null;
  tribeId: number | null; tribeTag: string | null; tribeColor: string | null;
}
interface ReportListItem { id: number; createdAt: number; title: string; outcome: string; seen: number }
interface Side { id: number; name: string; x: number; y: number; player: string | null }
interface Report {
  id: number; createdAt: number; title: string; outcome: string;
  attacker: Side; defender: Side; attackerWon: boolean;
  attackerUnits: Units; attackerLosses: Units;
  defenderUnits: Units | null; defenderLosses: Units | null;
  loot: Cost; wall: { before: number; after: number };
  loyalty: { before: number; after: number; conquered: boolean } | null;
  supported?: boolean;
  spy: { res: Cost; levels: Levels; unitsAfter: Units } | null;
}

type Tab = "dorf" | "kaserne" | "angriff" | "karte" | "berichte" | "stamm" | "rangliste" | "admin";
let rankMode: "spieler" | "staemme" = "spieler";
const mapCache = new Map<string, MapVillage>(); // "x|y" → Dorf, für die Warnung beim Angriff auf Stammesmitglieder

const RES_LABEL: Record<string, string> = { holz: "Holz", lehm: "Lehm", eisen: "Eisen" };
const $ = <T extends HTMLElement = HTMLElement>(sel: string) => document.querySelector(sel) as T;
const fmt = (n: number) => Math.floor(n).toLocaleString("de-DE");

let me: Me | null = null;
let village: Village | null = null;
let clockOffset = 0;
let tab: Tab = "dorf";
let refreshing = false;
let mapCenter: { x: number; y: number } | null = null;
let openReport: number | null = null;

const serverNow = () => Date.now() + clockOffset;

async function api<T>(url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method: body === undefined ? "GET" : "POST",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && !url.includes("login")) {
    showAuth();
    throw new Error(data.error ?? "Nicht eingeloggt");
  }
  if (!res.ok) throw new Error(data.error ?? `Fehler ${res.status}`);
  return data as T;
}

function toast(msg: string) {
  const el = $("#toast");
  el.textContent = msg;
  el.classList.add("show");
  clearTimeout((el as any)._t);
  (el as any)._t = setTimeout(() => el.classList.remove("show"), 2800);
}

function esc(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

function startText(t: number) {
  return new Date(t).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) + " Uhr";
}

/** Countdown „2 Tage 03:12:45“ */
function countdown(ms: number) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const d = Math.floor(s / 86400);
  return (d ? `${d} ${d === 1 ? "Tag" : "Tage"} ` : "") + formatDuration(s % 86400);
}

function prestart() {
  return !!me?.startAt && serverNow() < me.startAt;
}

function renderCountdown() {
  const el = $("#countdown");
  el.hidden = !prestart();
  if (el.hidden) return;
  el.innerHTML = `<span>Die Welt startet am <b>${startText(me!.startAt!)}</b></span>
    <strong class="cd">${countdown(me!.startAt! - serverNow())}</strong>
    <span class="muted">Bis dahin: umschauen, Stamm gründen, Pläne schmieden. Alle starten mit denselben Rohstoffen.</span>`;
}

function costHtml(cost: Cost, have?: Cost, extra = "") {
  return `<div class="cost">${RESOURCES.map(
    (k) => `<span class="c-${k} ${have && have[k] < cost[k] ? "short" : ""}">${fmt(cost[k])}</span>`,
  ).join("")}${extra}</div>`;
}

/** Ersetzt nur Zeilen, die sich geändert haben – sonst gehen Klicks im Sekundentakt verloren. */
function patchList(ul: HTMLElement, rows: string[]) {
  const cache: string[] = ((ul as any)._rows ??= []);
  if (ul.children.length !== rows.length || cache.length !== rows.length) {
    ul.innerHTML = rows.join("");
    (ul as any)._rows = rows.slice();
    return;
  }
  rows.forEach((html, i) => {
    if (cache[i] !== html) {
      ul.children[i].outerHTML = html;
      cache[i] = html;
    }
  });
}

/** Rendert neu, ohne dass Eingaben in Formularfeldern verloren gehen. */
function keepInputs(root: HTMLElement, fn: () => void) {
  const vals = new Map<string, string>();
  root.querySelectorAll<HTMLInputElement>("input[name]").forEach((i) => vals.set(i.name, i.value));
  const focused = (document.activeElement as HTMLInputElement | null)?.name;
  fn();
  root.querySelectorAll<HTMLInputElement>("input[name]").forEach((i) => {
    if (vals.has(i.name)) i.value = vals.get(i.name)!;
    if (i.name === focused) i.focus();
  });
}

// ---------- Auth ----------

async function showAuth() {
  $("#auth").hidden = false;
  $("#game").hidden = true;
  try {
    const pub = await api<{ registration: "open" | "code" | "closed"; announcement: string; startAt: number | null; serverTime: number }>(
      "/api/public",
    );
    const upcoming = pub.startAt && pub.startAt > pub.serverTime;
    $("#auth-start").hidden = !upcoming;
    if (upcoming) $("#auth-start").innerHTML = `Spielstart: <b>${startText(pub.startAt!)}</b>. Registrieren geht schon vorher.`;
    $("#code-wrap").hidden = pub.registration !== "code";
    $<HTMLButtonElement>("[data-auth=register]").hidden = pub.registration === "closed";
    $("#reg-closed").hidden = pub.registration !== "closed";
    $("#auth-announcement").hidden = !pub.announcement;
    $("#auth-announcement").textContent = pub.announcement;
  } catch {}
}

async function submitAuth(kind: "login" | "register") {
  const name = $<HTMLInputElement>("#name").value;
  const password = $<HTMLInputElement>("#password").value;
  try {
    await api(`/api/${kind}`, { name, password, code: $<HTMLInputElement>("#regcode").value });
    await start();
  } catch (e) {
    $("#auth-error").textContent = (e as Error).message;
  }
}

// ---------- Laden ----------

async function start() {
  try {
    me = await api<Me>("/api/me");
  } catch {
    return showAuth();
  }
  Object.assign(WORLD, me.world); // Server gibt das Welttempo vor
  $("#auth").hidden = true;
  $("#game").hidden = false;
  $("#player").textContent = me.user.name;
  $("#admin-tab").hidden = !me.user.isAdmin;
  let saved = 0;
  try {
    saved = Number(localStorage.getItem("koenigsschlacht-village")) || 0;
  } catch {}
  await loadVillage(saved);
}

/** Lädt ein Dorf; fällt auf das erste eigene Dorf zurück, wenn es nicht (mehr) dir gehört. */
async function loadVillage(id: number) {
  me = await api<Me>("/api/me");
  renderSwitcher();
  $("#announcement").hidden = !me.announcement;
  $("#announcement").textContent = me.announcement;
  document.body.classList.toggle("prestart", !!me.startAt);
  if (!me.villages.length) return showRestart();
  $("#restart").hidden = true;
  $("#village-ui").hidden = false;
  const target = me.villages.some((v) => v.id === id) ? id : me.villages[0].id;
  if (village && village.id !== target && village.id === id) toast("Dieses Dorf gehört dir nicht mehr!");
  try {
    localStorage.setItem("koenigsschlacht-village", String(target));
  } catch {}
  setVillage(await api<Village>(`/api/village/${target}`));
}

function showRestart() {
  village = null;
  $("#vname").textContent = "Königsschlacht";
  $("#vcoords").textContent = "";
  $("#loyalty").textContent = "";
  $("#loyalty").classList.remove("low");
  $("#village-ui").hidden = true;
  $("#restart").hidden = false;
}

function renderSwitcher() {
  const sel = $<HTMLSelectElement>("#village-select");
  sel.hidden = !me || me.villages.length < 2;
  sel.innerHTML = (me?.villages ?? [])
    .map((v) => `<option value="${v.id}" ${v.id === village?.id ? "selected" : ""}>${esc(v.name)} (${v.x}|${v.y})</option>`)
    .join("");
}

function setVillage(v: Village) {
  lastLoad = Date.now();
  village = v;
  clockOffset = v.serverTime - Date.now();
  mapCenter ??= { x: v.x, y: v.y };
  render();
}

// ---------- Hilfen ----------

function liveResources(): Cost {
  const v = village!;
  return resourcesAt(v.resources, v.resAt, serverNow(), v.levels);
}

function plannedLevels(): Levels {
  const p = { ...village!.levels };
  for (const q of village!.queue) p[q.building] = Math.max(p[q.building], q.level);
  return p;
}

function troopPop(): number {
  const v = village!;
  const queued = v.recruitQueue.reduce((s, r) => s + (r.count - r.delivered) * UNITS[r.unit].pop, 0);
  return unitPop(v.units) + unitPop(v.unitsAway) + queued;
}

/** Nächster Zeitpunkt, an dem sich serverseitig etwas ändert. */
function nextChangeAt(): number {
  const v = village!;
  const times = [
    ...v.queue.map((q) => q.endsAt),
    ...v.movements.map((m) => m.endsAt),
    ...v.recruitQueue.map((r) => r.startsAt + (r.delivered + 1) * r.per),
  ];
  return times.length ? Math.min(...times) : Infinity;
}

// ---------- Darstellung ----------

function render() {
  if (!village) return;
  const v = village;
  $("#vname").textContent = v.name;
  $("#vcoords").textContent = `(${v.x}|${v.y})`;
  $("#loyalty").textContent = `Zustimmung ${v.loyalty}`;
  $("#points").textContent = `${fmt(v.points)} Punkte`;
  $("#loyalty").classList.toggle("low", v.loyalty < 100);
  const sel = $<HTMLSelectElement>("#village-select");
  if (sel.value !== String(v.id)) sel.value = String(v.id);
  document.querySelectorAll<HTMLButtonElement>(".tabs button").forEach((b) => b.classList.toggle("active", b.dataset.tab === tab));
  updateBadge();
  renderCountdown();
  const incoming = v.movements.filter((m) => m.kind === "incoming").length;
  const alarm = $("#alarm");
  alarm.hidden = incoming === 0;
  alarm.textContent = incoming === 1 ? "1 Angriff im Anmarsch!" : `${incoming} Angriffe im Anmarsch!`;

  for (const t of ["dorf", "kaserne", "angriff", "karte", "berichte", "stamm", "rangliste", "admin"] as Tab[]) $(`#view-${t}`).hidden = tab !== t;
  renderResources();
  if (tab === "dorf") renderDorf();
  if (tab === "kaserne") renderKaserne();
  if (tab === "angriff") renderAngriff();
  if (tab === "karte") renderMap();
  if (tab === "berichte") renderReports();
  if (tab === "rangliste") renderRanking();
  if (tab === "admin") {
    const active = document.activeElement;
    const typing = active && $("#admin").contains(active) && /^(INPUT|TEXTAREA)$/.test(active.tagName);
    if (!typing && !adminPw) renderAdmin();
  }
  // Beim automatischen Nachladen nicht neu zeichnen, während jemand im Stamm-Tab tippt
  if (tab === "stamm") {
    const active = document.activeElement;
    const typing = active && $("#tribe").contains(active) && /^(INPUT|TEXTAREA)$/.test(active.tagName);
    if (!typing) renderTribe();
  }
}

function updateBadge() {
  const badge = $("#report-badge");
  badge.textContent = String(village!.unreadReports);
  badge.hidden = village!.unreadReports === 0;
  const tb = $("#tribe-badge");
  tb.textContent = String(village!.tribeBadge);
  tb.hidden = village!.tribeBadge === 0 || tab === "stamm";
}

function renderResources() {
  const v = village!;
  const res = liveResources();
  const cap = storageCapacity(v.levels.speicher);
  const r = rates(v.levels);
  $("#resources").innerHTML =
    RESOURCES.map(
      (k) => `<div class="res res-${k}" title="${r[k]} pro Stunde">
        <span class="res-icon"></span>
        <span class="res-label">${RES_LABEL[k]}</span>
        <b class="${res[k] >= cap ? "full" : ""}">${fmt(res[k])}</b>
        <small>+${r[k]}/h</small>
      </div>`,
    ).join("") +
    `<div class="res res-cap"><span class="res-label">Speicher</span><b>${fmt(cap)}</b></div>
     <div class="res res-pop"><span class="res-label">Bevölkerung</span><b>${popUsed(v.levels) + troopPop()}/${farmCapacity(v.levels.bauernhof)}</b></div>`;
}

function unitList(u: Partial<Units>) {
  const parts = UNIT_IDS.filter((id) => (u[id] ?? 0) > 0).map((id) => `${fmt(u[id]!)} ${UNITS[id].name}`);
  return parts.length ? parts.join(", ") : "–";
}

function renderDorf() {
  const v = village!;
  const now = serverNow();

  // Truppenbewegungen
  $("#movements-wrap").hidden = v.movements.length === 0;
  patchList(
    $("#movements"),
    v.movements.map((m) => {
      const where = `${esc(m.target?.name ?? "?")} (${m.target?.x}|${m.target?.y})`;
      const label = {
        incoming: `Angriff von ${where}`,
        attack: `Angriff auf ${where}`,
        support: `Unterstützung für ${where}`,
        "support-in": `Unterstützung von ${where}`,
        return: `Rückkehr von ${esc(m.target?.name ?? "?")}`,
      }[m.kind];
      const detail =
        m.kind === "incoming"
          ? "Truppen unbekannt"
          : unitList(m.units ?? {}) + (m.loot && totalLoot(m.loot) > 0 ? ` · Beute ${fmt(totalLoot(m.loot))}` : "");
      // Zurückrufen-Knopf: Restzeit auf ganze 10 s gerundet, damit die Zeile nicht jede Sekunde neu gebaut wird
      const canRecall = m.id && m.cancelUntil && now < m.cancelUntil && now < m.endsAt;
      const recall = canRecall ? `<button class="link" data-recall="${m.id}">Zurückrufen</button>` : "";
      return `<li class="mv mv-${m.kind}">
        <div class="q-head"><span>${label}</span><span class="q-time">${formatDuration((m.endsAt - now) / 1000)}</span>${recall}</div>
        <div class="mv-detail">${detail}</div>
      </li>`;
    }),
  );

  // Bauschleife
  patchList(
    $("#queue"),
    v.queue.length
      ? v.queue.map((q, i) => {
          const done = Math.min(1, Math.max(0, (now - q.startsAt) / (q.endsAt - q.startsAt)));
          return `<li>
            <div class="q-head"><span>${BUILDINGS[q.building].name} <em>Stufe ${q.level}</em></span>
              <span class="q-time">${formatDuration((q.endsAt - now) / 1000)}</span>
              ${i === v.queue.length - 1 ? `<button class="link" data-cancel>Abbrechen</button>` : ""}</div>
            <div class="bar"><i style="width:${(done * 100).toFixed(1)}%"></i></div>
          </li>`;
        })
      : [`<li class="empty">Nichts im Bau. Die Arbeiter warten.</li>`],
  );

  // Gebäude
  const planned = plannedLevels();
  const res = liveResources();
  const r = rates(v.levels);
  const tp = troopPop();
  patchList(
    $("#buildings"),
    BUILDING_IDS.map((id) => {
      const d = BUILDINGS[id];
      const level = v.levels[id];
      const blocker = buildBlocker(id, planned, tp);
      const next = planned[id] + 1;
      let action = "";
      let cost = "";
      if (blocker === "Maximale Stufe erreicht") {
        action = `<span class="muted">Voll ausgebaut</span>`;
      } else if (prestart()) {
        const c = buildCost(id, next);
        cost = costHtml(c, res, `<span class="c-time">${formatDuration(buildTime(id, next, planned.hauptgebaeude))}</span>`);
        action = `<span class="muted">Ab Spielstart</span>`;
      } else {
        const c = buildCost(id, next);
        cost = costHtml(c, res, `<span class="c-time">${formatDuration(buildTime(id, next, planned.hauptgebaeude))}</span>`);
        let reason = blocker;
        if (!reason && v.queue.length >= WORLD.maxQueue) reason = "Bauschleife voll";
        if (!reason && !canAfford(res, c)) reason = waitReason(c, res, r);
        action = reason ? `<span class="muted">${esc(reason)}</span>` : `<button data-build="${id}">Stufe ${next}</button>`;
      }
      return `<li class="${level === 0 ? "unbuilt" : ""}">
        <div class="b-info"><span class="b-name">${d.name}</span> <span class="b-level">${level ? `Stufe ${level}` : "nicht gebaut"}</span>
          <p>${d.description}</p></div>
        ${cost}
        <div class="b-action">${action}</div>
      </li>`;
    }),
  );
}

function totalLoot(c: Cost) {
  return c.holz + c.lehm + c.eisen;
}

function waitReason(cost: Cost, res: Cost, r: Cost): string {
  const cap = storageCapacity(village!.levels.speicher);
  if (RESOURCES.some((k) => cost[k] > cap)) return "Speicher zu klein";
  const wait = Math.max(...RESOURCES.map((k) => (cost[k] - res[k]) / r[k])) * 3600;
  return `Genug in ${formatDuration(wait)}`;
}

// ---------- Kaserne ----------

function renderKaserne() {
  const v = village!;
  const k = v.levels.kaserne;
  $("#kaserne-locked").hidden = k > 0;
  $("#kaserne-body").hidden = k === 0;
  if (k === 0) return;
  const owned = v.villagesOwned;
  renderRecruitQueue();
  const res = liveResources();
  const free = farmCapacity(v.levels.bauernhof) - popUsed(v.levels) - troopPop();
  keepInputs($("#units"), () => {
    $("#units").innerHTML = UNIT_IDS.map((id) => {
      const d = UNITS[id];
      const bLevel = v.levels[d.building];
      const locked = bLevel < d.level;
      const cost = unitCost(id, owned);
      const max = Math.max(0, Math.min(Math.floor(free / d.pop), ...RESOURCES.map((r) => Math.floor(res[r] / cost[r]))));
      const away = v.unitsAway[id] ? ` <span class="muted">(+${fmt(v.unitsAway[id])} unterwegs)</span>` : "";
      return `<li class="${locked ? "unbuilt" : ""}">
        <div class="b-info"><span class="b-name">${d.name}</span> <span class="b-level">${fmt(v.units[id])} im Dorf${away}</span>
          <p>${d.description} <span class="stats">⚔ ${d.attack} · 🛡 ${d.defInf}/${d.defCav} · 👣 ${d.speed} min · 🎒 ${d.carry}</span></p></div>
        ${costHtml(cost, res, `<span class="c-time">${formatDuration(recruitTime(id, Math.max(1, bLevel)))}</span>`)}
        <div class="b-action">${
          locked
            ? `<span class="muted">${BUILDINGS[d.building].name} Stufe ${d.level}</span>`
            : `<form class="recruit" data-recruit="${id}">
                 <input name="r-${id}" type="number" min="1" inputmode="numeric" placeholder="0" />
                 <button type="button" class="link" data-max="${id}" data-maxv="${max}">(${fmt(max)})</button>
                 <button type="submit">Ausbilden</button>
               </form>`
        }</div>
      </li>`;
    }).join("");
  });
}

function renderRecruitQueue() {
  const v = village!;
  const now = serverNow();
  patchList(
    $("#recruit-queue"),
    v.recruitQueue.length
      ? v.recruitQueue.map((r, i) => {
          const done = Math.min(r.count, Math.max(0, Math.floor((now - r.startsAt) / r.per)));
          return `<li>
            <div class="q-head"><span>${fmt(r.count - done)} ${UNITS[r.unit].name}</span>
              <span class="q-time">${formatDuration((r.endsAt - now) / 1000)}</span>
              ${i === v.recruitQueue.length - 1 ? `<button class="link" data-cancel-recruit>Abbrechen</button>` : ""}</div>
            <div class="bar"><i style="width:${((done / r.count) * 100).toFixed(1)}%"></i></div>
          </li>`;
        })
      : [`<li class="empty">Keine Ausbildung.</li>`],
  );
}

// ---------- Angriff ----------

function renderAngriff() {
  const v = village!;
  keepInputs($("#attack-form"), () => {
    $("#attack-units").innerHTML = UNIT_IDS.map(
      (id) => `<label class="unit-input">
        <span>${UNITS[id].name}</span>
        <input name="a-${id}" type="number" min="0" inputmode="numeric" placeholder="0" />
        <button type="button" class="link" data-all="${id}">${fmt(v.units[id])}</button>
      </label>`,
    ).join("");
  });
  updateAttackPreview();
  renderSupportLists();
}

function renderSupportLists() {
  const v = village!;
  const row = (s: SupportEntry, action: string) => `<li>
      <div class="q-head"><span>${esc(s.village.name)} (${s.village.x}|${s.village.y})
        <em>${esc(s.village.player ?? "verlassen")}</em></span>
        <button class="link" data-withdraw="${s.id}">${action}</button></div>
      <div class="mv-detail">${unitList(s.units)}</div>
    </li>`;
  $("#support-here").innerHTML = v.supportHere.length
    ? v.supportHere.map((s) => row(s, "Zurückschicken")).join("")
    : `<li class="empty">Keine fremden Truppen im Dorf.</li>`;
  $("#support-out").innerHTML = v.supportOut.length
    ? v.supportOut.map((s) => row(s, "Zurückziehen")).join("")
    : `<li class="empty">Keine Truppen in anderen Dörfern.</li>`;
}

function attackUnits(): Units {
  const raw: Record<string, string> = {};
  for (const id of UNIT_IDS) raw[id] = $<HTMLInputElement>(`[name=a-${id}]`)?.value ?? "0";
  return cleanUnits(raw);
}

function updateAttackPreview() {
  const v = village!;
  const x = Number($<HTMLInputElement>("[name=tx]").value);
  const y = Number($<HTMLInputElement>("[name=ty]").value);
  const units = attackUnits();
  const el = $("#attack-preview");
  if (!$<HTMLInputElement>("[name=tx]").value || !$<HTMLInputElement>("[name=ty]").value || totalUnits(units) === 0) {
    el.textContent = "Ziel und Truppen wählen.";
    return;
  }
  const d = distance(v, { x, y });
  const t = travelTime(units, d);
  const arrive = new Date(serverNow() + t * 1000).toLocaleTimeString("de-DE");
  el.innerHTML = `Entfernung <b>${d.toFixed(1)}</b> Felder · Laufzeit <b>${formatDuration(t)}</b> · Ankunft <b>${arrive}</b> · Tragkraft <b>${fmt(carryCapacity(units))}</b>`;
}

// ---------- Karte ----------

async function renderMap() {
  const v = village!;
  const c = mapCenter ?? v;
  const R = 7;
  const area = await api<MapVillage[]>(`/api/map?x=${c.x}&y=${c.y}&r=${R}`);
  const byPos = new Map(area.map((m) => [`${m.x}|${m.y}`, m]));
  for (const [k, m] of byPos) mapCache.set(k, m);
  let html = "";
  for (let y = c.y - R; y <= c.y + R; y++) {
    for (let x = c.x - R; x <= c.x + R; x++) {
      const m = byPos.get(`${x}|${y}`);
      const out = x < 0 || y < 0 || x >= WORLD.mapSize || y >= WORLD.mapSize;
      let cls = out ? "out" : "empty";
      let style = "";
      if (m) {
        const ally = m.ownerId !== me!.user.id && m.tribeId !== null && m.tribeId === me!.user.tribeId;
        cls = m.id === v.id ? "current" : m.ownerId === me!.user.id ? "own" : ally ? "ally" : m.ownerId ? "player" : "barb";
        if (cls === "player" && m.tribeColor) style = ` style="background:${m.tribeColor}"`;
      }
      const owner = m ? (m.ownerName ? `${m.ownerName}${m.tribeTag ? ` [${m.tribeTag}]` : ""}` : "verlassen") : "";
      const data = m
        ? `data-vx="${x}" data-vy="${y}" data-vname="${esc(m.name)}" data-vowner="${esc(owner)}" data-vid="${m.id}" data-vbarb="${m.ownerId ? 0 : 1}"`
        : "";
      html += `<div class="cell ${cls}"${style} title="${m ? esc(`${m.name} (${x}|${y}) – ${owner}`) : `(${x}|${y})`}" ${data}></div>`;
    }
  }
  $("#map").innerHTML = html;
  $("#map-center").textContent = `(${c.x}|${c.y})`;
}

function showMapInfo(el: HTMLElement) {
  const { vx, vy, vname, vowner, vid } = el.dataset;
  const current = Number(vid) === village!.id;
  const mine = me!.villages.some((v) => v.id === Number(vid));
  $("#map-info").innerHTML = `<b>${esc(vname!)}</b> (${vx}|${vy}) · ${esc(vowner!)} ${
    current
      ? ""
      : mine
        ? `<button class="secondary" data-switch="${vid}">Zu diesem Dorf</button>
           <button class="secondary" data-target-x="${vx}" data-target-y="${vy}">Unterstützen</button>`
        : `<button data-target-x="${vx}" data-target-y="${vy}">Angreifen</button>${
            el.dataset.vbarb === "1" ? "" : ` <button class="secondary" data-target-x="${vx}" data-target-y="${vy}">Unterstützen</button>`
          }`
  }`;
}

// ---------- Rangliste ----------

function tagChip(tag: string | null, color: string | null) {
  return tag ? `<span class="tag" style="--tc:${color ?? "var(--muted)"}">${esc(tag)}</span>` : "";
}

async function renderRanking() {
  document.querySelectorAll<HTMLButtonElement>("[data-rank]").forEach((b) => b.classList.toggle("active", b.dataset.rank === rankMode));
  if (rankMode === "staemme") {
    const rows = await api<TribeRankRow[]>("/api/ranking/tribes");
    $("#ranking").innerHTML = rows.length
      ? `<table class="ranking">
      <thead><tr><th>#</th><th>Stamm</th><th>Punkte</th><th>Mitglieder</th><th>Dörfer</th><th>Besiegt (Angr.)</th><th>Besiegt (Vert.)</th></tr></thead>
      <tbody>${rows
        .map(
          (r) => `<tr class="${r.id === me!.user.tribeId ? "me" : ""}"><td>${r.rank}</td><td>${tagChip(r.tag, r.color)} ${esc(r.name)}</td>
            <td>${fmt(r.points)}</td><td>${r.members}</td><td>${r.villages}</td><td>${fmt(r.killsAtt)}</td><td>${fmt(r.killsDef)}</td></tr>`,
        )
        .join("")}</tbody></table>`
      : `<p class="note">Noch keine Stämme gegründet.</p>`;
    return;
  }
  const rows = await api<RankRow[]>("/api/ranking");
  $("#ranking").innerHTML = `<table class="ranking">
    <thead><tr><th>#</th><th>Spieler</th><th>Punkte</th><th>Dörfer</th>
      <th title="Besiegte Bevölkerung als Angreifer">Besiegt (Angr.)</th><th title="Besiegte Bevölkerung als Verteidiger">Besiegt (Vert.)</th></tr></thead>
    <tbody>${rows
      .map(
        (r) => `<tr class="${r.id === me!.user.id ? "me" : ""}"><td>${r.rank}</td><td>${esc(r.name)} ${tagChip(r.tribeTag, r.tribeColor)}</td><td>${fmt(r.points)}</td>
          <td>${r.villages}</td><td>${fmt(r.killsAtt)}</td><td>${fmt(r.killsDef)}</td></tr>`,
      )
      .join("")}</tbody></table>`;
}

// ---------- Stamm ----------

let tribeData: TribeOverview | null = null;

async function renderTribe(data?: TribeOverview) {
  // Eingaben (Pinnwand-Entwurf, Einladungsname) über das Neuzeichnen retten
  const draft = $<HTMLTextAreaElement>("#tribe-post")?.value ?? "";
  const t = data ?? (await api<TribeOverview>("/api/tribe"));
  tribeData = t;
  me!.user.tribeId = t.tribe?.id ?? null;
  if (village) {
    village.tribeBadge = 0;
    updateBadge();
  }
  const el = $("#tribe");
  const invitesForMe = t.myInvites.length
    ? `<h2>Einladungen</h2><ul class="queue">${t.myInvites
        .map(
          (i) => `<li><div class="q-head"><span>${tagChip(i.tag, i.color)} ${esc(i.name)}</span>
            <button data-tribe-act="accept" data-invite="${i.id}">Annehmen</button>
            <button class="secondary" data-tribe-act="decline" data-invite="${i.id}">Ablehnen</button></div></li>`,
        )
        .join("")}</ul>`
    : "";

  if (!t.tribe) {
    el.innerHTML = `${invitesForMe}
      <form id="tribe-create" class="card tribe-form">
        <h2>Stamm gründen</h2>
        <label>Name <input name="tname" maxlength="30" placeholder="z. B. Die Seeleute" required /></label>
        <label>Kürzel <input name="ttag" maxlength="6" placeholder="SEE" required /></label>
        <fieldset class="swatches"><legend>Farbe auf der Karte</legend>
          ${t.colors
            .map(
              (c, i) => `<label class="swatch"><input type="radio" name="tcolor" value="${c}" ${i === 0 ? "checked" : ""} />
                <span style="background:${c}"></span></label>`,
            )
            .join("")}
        </fieldset>
        <button type="submit">Gründen</button>
      </form>
      <p class="muted legend-line">Oder lass dich von einem Anführer einladen.</p>`;
    return;
  }

  const tr = t.tribe;
  const leader = t.role === "leader";
  const members = t.members!;
  el.innerHTML = `${invitesForMe}
    <div class="tribe-head">
      ${tagChip(tr.tag, tr.color)}
      <h2>${esc(tr.name)}</h2>
      <span class="muted">${members.length} ${members.length === 1 ? "Mitglied" : "Mitglieder"} · ${fmt(members.reduce((s, m) => s + m.points, 0))} Punkte</span>
    </div>
    ${
      leader
        ? `<form id="tribe-desc" class="tribe-desc"><textarea name="tdesc" rows="2" maxlength="1000" placeholder="Beschreibung, Regeln, Ziele …">${esc(tr.description)}</textarea>
           <button type="submit" class="secondary">Speichern</button></form>`
        : tr.description
          ? `<p class="tribe-desc-text">${esc(tr.description)}</p>`
          : ""
    }

    <h2>Pinnwand</h2>
    <ul class="board">${
      t.messages!.length
        ? t.messages!
            .map(
              (m) => `<li class="${m.author ? "" : "sys"}">
                ${m.author ? `<b>${esc(m.author)}</b>` : ""}
                <span class="muted">${new Date(m.createdAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</span>
                <p>${esc(m.text)}</p></li>`,
            )
            .join("")
        : `<li class="sys"><p>Noch keine Beiträge.</p></li>`
    }</ul>
    <form id="tribe-post-form" class="board-form">
      <textarea id="tribe-post" rows="2" maxlength="500" placeholder="Nachricht an den Stamm …"></textarea>
      <button type="submit">Senden</button>
    </form>

    <h2>Mitglieder</h2>
    <table class="ranking members">
      <thead><tr><th>Spieler</th><th>Platz</th><th>Punkte</th><th>Dörfer</th>${leader ? "<th></th>" : ""}</tr></thead>
      <tbody>${members
        .map(
          (m) => `<tr class="${m.id === me!.user.id ? "me" : ""}"><td>${esc(m.name)}${m.role === "leader" ? ` <span class="role">Anführer</span>` : ""}</td>
            <td>${m.rank}</td><td>${fmt(m.points)}</td><td>${m.villages}</td>
            ${
              leader
                ? `<td class="actions">${
                    m.id === me!.user.id
                      ? ""
                      : `<button class="link" data-tribe-act="promote" data-user="${m.id}">Anführer machen</button>
                         <button class="link danger" data-tribe-act="kick" data-user="${m.id}">Entlassen</button>`
                  }</td>`
                : ""
            }</tr>`,
        )
        .join("")}</tbody>
    </table>

    ${
      leader
        ? `<h2>Spieler einladen</h2>
           <form id="tribe-invite" class="invite-form"><input name="iname" placeholder="Spielername" required /><button type="submit">Einladen</button></form>
           ${
             t.invites!.length
               ? `<ul class="queue">${t.invites!
                   .map(
                     (i) => `<li><div class="q-head"><span>${esc(i.name)} <em>eingeladen</em></span>
                       <button class="link" data-tribe-act="cancel-invite" data-invite="${i.id}">Zurückziehen</button></div></li>`,
                   )
                   .join("")}</ul>`
               : ""
           }`
        : ""
    }
    <p class="leave"><button class="secondary danger" data-tribe-act="leave">Stamm verlassen</button></p>`;
  $<HTMLTextAreaElement>("#tribe-post").value = draft;
  const board = el.querySelector(".board");
  if (board) board.scrollTop = board.scrollHeight;
}

async function tribeAction(action: string, body: Record<string, unknown> = {}) {
  try {
    await renderTribe(await api<TribeOverview>(`/api/tribe/${action}`, body));
    return true;
  } catch (err) {
    toast((err as Error).message);
    return false;
  }
}

// ---------- Admin ----------

interface AdminData {
  players: { id: number; name: string; createdAt: number; lastSeen: number | null; banned: boolean; admin: boolean; points: number; villages: number; tribeTag: string | null }[];
  settings: { registration: "open" | "code" | "closed"; registerCode: string; announcement: string; worldSpeed: number; startAt: number | null };
  stats: { players: number; online: number; villages: number; barbarians: number; tribes: number; movements: number; dbSize: number };
  password?: string;
}
let adminPw: { name: string; password: string } | null = null;

function ago(t: number | null) {
  if (!t) return "nie";
  const m = Math.floor((Date.now() - t) / 60_000);
  if (m < 2) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  const h = Math.floor(m / 60);
  if (h < 48) return `vor ${h} Std.`;
  return `vor ${Math.floor(h / 24)} Tagen`;
}

async function renderAdmin(data?: AdminData) {
  const d = data ?? (await api<AdminData>("/api/admin"));
  const st = d.stats;
  const s = d.settings;
  const stat = (label: string, value: string) => `<div class="stat"><span>${label}</span><b>${value}</b></div>`;
  $("#admin").innerHTML = `
    <div class="stats">
      ${stat("Spieler", String(st.players))}${stat("Online (15 Min.)", String(st.online))}
      ${stat("Spielerdörfer", String(st.villages))}${stat("Verlassene", String(st.barbarians))}
      ${stat("Stämme", String(st.tribes))}${stat("Truppen unterwegs", String(st.movements))}
      ${stat("Datenbank", `${(st.dbSize / 1024 / 1024).toFixed(1)} MB`)}
    </div>

    ${
      adminPw
        ? `<div class="card pw-box"><p>Neues Passwort für <b>${esc(adminPw.name)}</b>:</p>
           <p class="pw"><code>${esc(adminPw.password)}</code> <button class="secondary" data-admin-copy>Kopieren</button></p>
           <p class="muted">Gib es ${esc(adminPw.name)} weiter. Er kann es danach unter „Passwort“ selbst ändern.</p>
           <button class="link" data-admin-pwclose>Schließen</button></div>`
        : ""
    }

    <form id="admin-settings" class="card admin-card">
      <h2>Einstellungen</h2>
      <label>Ankündigung für alle Spieler <span class="muted">(leer = keine)</span>
        <textarea name="announcement" rows="2" maxlength="500" placeholder="z. B. Freitag 20 Uhr: Server-Update, ca. 5 Minuten offline">${esc(s.announcement)}</textarea></label>
      <fieldset class="radios"><legend>Registrierung</legend>
        <label><input type="radio" name="registration" value="open" ${s.registration === "open" ? "checked" : ""}/> Offen</label>
        <label><input type="radio" name="registration" value="code" ${s.registration === "code" ? "checked" : ""}/> Nur mit Einladungscode</label>
        <label><input type="radio" name="registration" value="closed" ${s.registration === "closed" ? "checked" : ""}/> Geschlossen</label>
      </fieldset>
      <label>Einladungscode <input name="registerCode" value="${esc(s.registerCode)}" maxlength="40" /></label>
      <label>Spielstart <span class="muted">(leer = sofort spielbar; davor stehen Produktion und alle Aktionen still)</span>
        <span class="start-row"><input name="startAt" type="datetime-local" value="${s.startAt ? localInput(s.startAt) : ""}" />
        <button type="button" class="link" data-clear-start>Leeren</button></span></label>
      ${s.startAt && s.startAt > Date.now() ? `<p class="muted start-info">Startet in ${countdown(s.startAt - Date.now())}</p>` : ""}
      <label>Welttempo <span class="muted">(1 = normal, wirkt sofort auf Produktion und neue Aufträge)</span>
        <input name="worldSpeed" type="number" min="0.1" max="1000" step="0.1" value="${s.worldSpeed}" /></label>
      <button type="submit">Speichern</button>
    </form>

    <h2>Spieler</h2>
    <div class="ranking-wrap"><table class="ranking admin-players">
      <thead><tr><th>Spieler</th><th>Punkte</th><th>Dörfer</th><th>Zuletzt online</th><th></th></tr></thead>
      <tbody>${d.players
        .map(
          (p) => `<tr class="${p.banned ? "banned" : ""}">
            <td>${esc(p.name)} ${p.tribeTag ? `<span class="muted">[${esc(p.tribeTag)}]</span>` : ""}
              ${p.admin ? `<span class="role">Admin</span>` : ""}${p.banned ? `<span class="role danger">gesperrt</span>` : ""}</td>
            <td>${fmt(p.points)}</td><td>${p.villages}</td><td>${ago(p.lastSeen)}</td>
            <td class="actions">${
              p.id === me!.user.id
                ? ""
                : `<button class="link" data-admin-act="reset-password" data-user="${p.id}" data-name="${esc(p.name)}">Passwort zurücksetzen</button>
                   <button class="link" data-admin-act="${p.banned ? "unban" : "ban"}" data-user="${p.id}" data-name="${esc(p.name)}">${p.banned ? "Entsperren" : "Sperren"}</button>
                   <button class="link danger" data-admin-act="delete" data-user="${p.id}" data-name="${esc(p.name)}">Löschen</button>`
            }</td></tr>`,
        )
        .join("")}</tbody>
    </table></div>

    <form id="admin-reset" class="card admin-card danger-zone">
      <h2>Welt zurücksetzen</h2>
      <p>Löscht alle Dörfer, Truppen, Berichte und Stämme. Gut für einen Neustart nach einer Testrunde.</p>
      <label class="check"><input type="checkbox" name="keepAccounts" checked /> Accounts behalten (jeder bekommt ein neues Dorf)</label>
      <label>Zur Bestätigung <b>ZURÜCKSETZEN</b> eintippen <input name="confirm" autocomplete="off" /></label>
      <button type="submit" class="danger-btn">Welt zurücksetzen</button>
    </form>`;
}

/** ms → Wert für <input type="datetime-local"> in lokaler Zeit */
function localInput(t: number) {
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

async function adminAction(action: string, body: Record<string, unknown>) {
  try {
    const d = await api<AdminData>(`/api/admin/${action}`, body);
    await renderAdmin(d);
    return d;
  } catch (err) {
    toast((err as Error).message);
    return null;
  }
}

// ---------- Berichte ----------

async function renderReports() {
  if (openReport !== null) return renderReport(openReport);
  const list = await api<ReportListItem[]>("/api/reports");
  $("#report-detail").hidden = true;
  $("#reports").hidden = false;
  $("#reports").innerHTML = list.length
    ? list
        .map(
          (r) => `<li class="report ${r.seen ? "" : "unseen"}" data-report="${r.id}">
            <span class="dot dot-${r.outcome}"></span>
            <span class="r-title">${esc(r.title)}</span>
            <span class="muted">${new Date(r.createdAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</span>
          </li>`,
        )
        .join("")
    : `<li class="empty">Noch keine Berichte.</li>`;
}

function unitTable(rows: [string, Partial<Units> | null][]) {
  const shown = UNIT_IDS.filter((id) => rows.some(([, u]) => u && (u[id] ?? 0) > 0));
  if (!shown.length) return `<p class="muted">Keine Truppen.</p>`;
  return `<table class="units-table"><tr><th></th>${shown.map((id) => `<th>${UNITS[id].name}</th>`).join("")}</tr>
    ${rows.map(([label, u]) => `<tr><td>${label}</td>${shown.map((id) => `<td>${u ? fmt(u[id] ?? 0) : "?"}</td>`).join("")}</tr>`).join("")}
  </table>`;
}

async function renderReport(id: number) {
  const r = await api<Report>(`/api/reports/${id}`);
  $("#reports").hidden = true;
  const el = $("#report-detail");
  el.hidden = false;
  const side = (s: Side) => `${esc(s.name)} (${s.x}|${s.y}) <span class="muted">${esc(s.player ?? "verlassen")}</span>`;
  el.innerHTML = `
    <button class="link" data-back>← Alle Berichte</button>
    <h3 class="outcome-${r.outcome}">${r.attackerWon ? "Der Angreifer hat gewonnen" : "Der Verteidiger hat gewonnen"}</h3>
    <p class="muted">${new Date(r.createdAt).toLocaleString("de-DE")}</p>
    <h4>Angreifer: ${side(r.attacker)}</h4>
    ${unitTable([["Anzahl", r.attackerUnits], ["Verluste", r.attackerLosses]])}
    <h4>Verteidiger: ${side(r.defender)}${r.supported ? ` <span class="muted">inkl. Unterstützung</span>` : ""}</h4>
    ${r.defenderUnits ? unitTable([["Anzahl", r.defenderUnits], ["Verluste", r.defenderLosses]]) : `<p class="muted">Keine Informationen – kein Angreifer hat überlebt.</p>`}
    ${r.wall.before !== r.wall.after ? `<p>Wall beschädigt: Stufe ${r.wall.before} → ${r.wall.after}</p>` : ""}
    ${
      r.loyalty
        ? r.loyalty.conquered
          ? `<p class="conquest">Zustimmung ${r.loyalty.before} → 0 · <b>Das Dorf wurde erobert!</b></p>`
          : `<p>Zustimmung gesenkt: ${r.loyalty.before} → ${r.loyalty.after}</p>`
        : ""
    }
    ${totalLoot(r.loot) > 0 ? `<h4>Beute</h4>${costHtml(r.loot)}` : ""}
    ${
      r.spy
        ? `<h4>Spähbericht</h4>
           <p>Rohstoffe:</p>${costHtml(r.spy.res)}
           <p>Gebäude: ${BUILDING_IDS.filter((b) => r.spy!.levels[b] > 0).map((b) => `${BUILDINGS[b].name} ${r.spy!.levels[b]}`).join(", ")}</p>
           <p>Truppen danach: ${unitList(r.spy.unitsAfter)}</p>`
        : ""
    }`;
  // Badge aktualisieren, ohne alles neu zu laden
  const list = await api<ReportListItem[]>("/api/reports");
  village!.unreadReports = list.filter((x) => !x.seen).length;
  updateBadge();
}

// ---------- Events ----------

document.addEventListener("click", async (e) => {
  const t = e.target as HTMLElement;
  const authBtn = t.closest<HTMLElement>("[data-auth]");
  if (authBtn) return submitAuth(authBtn.dataset.auth as "register");
  const tabBtn = t.closest<HTMLElement>("[data-tab]");
  if (tabBtn) {
    tab = tabBtn.dataset.tab as Tab;
    if (tab === "berichte") openReport = null;
    if (tab === "karte") mapCenter = { x: village!.x, y: village!.y };
    return render();
  }
  if (t.dataset.vx) return showMapInfo(t);
  if (t.hasAttribute("data-clear-start")) {
    $<HTMLInputElement>("[name=startAt]").value = "";
    return;
  }
  if (t.dataset.adminAct) {
    const act = t.dataset.adminAct;
    const name = t.dataset.name!;
    const ask: Record<string, string> = {
      "reset-password": `Neues Passwort für ${name} erzeugen? Das alte gilt dann nicht mehr.`,
      ban: `${name} sperren? Er wird sofort abgemeldet.`,
      delete: `${name} endgültig löschen? Seine Dörfer werden zu verlassenen Dörfern.`,
    };
    if (ask[act] && !confirm(ask[act])) return;
    const d = await adminAction(act, { user: Number(t.dataset.user) });
    if (d?.password) {
      adminPw = { name, password: d.password };
      renderAdmin(d);
    } else if (d) toast("Erledigt");
    return;
  }
  if (t.hasAttribute("data-admin-copy") && adminPw) {
    navigator.clipboard?.writeText(adminPw.password).then(() => toast("Kopiert"), () => toast("Bitte manuell kopieren"));
    return;
  }
  if (t.hasAttribute("data-admin-pwclose")) {
    adminPw = null;
    return renderAdmin();
  }
  if (t.id === "pw-open") {
    $<HTMLFormElement>("#pw-form").reset();
    return $<HTMLDialogElement>("#pw-dialog").showModal();
  }
  if (t.id === "pw-cancel") return $<HTMLDialogElement>("#pw-dialog").close();
  if (t.dataset.rank) {
    rankMode = t.dataset.rank as typeof rankMode;
    return renderRanking();
  }
  if (t.dataset.tribeAct) {
    const act = t.dataset.tribeAct;
    const name = (id?: string) => tribeData?.members?.find((m) => m.id === Number(id))?.name ?? "";
    if (act === "leave" && !confirm("Stamm wirklich verlassen?")) return;
    if (act === "kick" && !confirm(`${name(t.dataset.user)} wirklich entlassen?`)) return;
    if (act === "promote" && !confirm(`${name(t.dataset.user)} zum Anführer machen? Du wirst dann normales Mitglied.`)) return;
    return tribeAction(act, { invite: Number(t.dataset.invite), user: Number(t.dataset.user) });
  }
  if (t.dataset.switch) {
    tab = "dorf";
    mapCenter = null;
    return loadVillage(Number(t.dataset.switch));
  }
  if (t.id === "restart-btn") {
    try {
      const { id } = await api<{ id: number }>("/api/restart", {});
      return loadVillage(id);
    } catch (err) {
      return toast((err as Error).message);
    }
  }
  if (t.dataset.pan && mapCenter) {
    const [dx, dy] = t.dataset.pan.split(",").map(Number);
    mapCenter = { x: mapCenter.x + dx * 5, y: mapCenter.y + dy * 5 };
    return renderMap();
  }
  if (t.dataset.targetX) {
    tab = "angriff";
    render();
    $<HTMLInputElement>("[name=tx]").value = t.dataset.targetX;
    $<HTMLInputElement>("[name=ty]").value = t.dataset.targetY!;
    return updateAttackPreview();
  }
  if (t.dataset.all) {
    $<HTMLInputElement>(`[name=a-${t.dataset.all}]`).value = String(village!.units[t.dataset.all as UnitId]);
    return updateAttackPreview();
  }
  if (t.dataset.max) {
    $<HTMLInputElement>(`[name=r-${t.dataset.max}]`).value = t.dataset.maxv!;
    return;
  }
  const rep = t.closest<HTMLElement>("[data-report]");
  if (rep) {
    openReport = Number(rep.dataset.report);
    return renderReports();
  }
  if (t.hasAttribute("data-back")) {
    openReport = null;
    return loadVillage(village!.id);
  }
  try {
    if (t.dataset.build) {
      setVillage(await api<Village>(`/api/village/${village!.id}/build`, { building: t.dataset.build }));
    } else if (t.hasAttribute("data-cancel")) {
      setVillage(await api<Village>(`/api/village/${village!.id}/cancel`, {}));
      toast("Auftrag abgebrochen, Rohstoffe erstattet");
    } else if (t.dataset.recall) {
      setVillage(await api<Village>(`/api/village/${village!.id}/recall`, { event: Number(t.dataset.recall) }));
      toast("Truppen kehren um");
    } else if (t.dataset.withdraw) {
      setVillage(await api<Village>(`/api/village/${village!.id}/withdraw`, { support: Number(t.dataset.withdraw) }));
      toast("Truppen sind auf dem Heimweg");
    } else if (t.hasAttribute("data-cancel-recruit")) {
      setVillage(await api<Village>(`/api/village/${village!.id}/cancel-recruit`, {}));
      toast("Ausbildung abgebrochen, Rohstoffe erstattet");
    } else if (t.id === "vname") {
      const name = prompt("Neuer Dorfname", village!.name);
      if (name) setVillage(await api<Village>(`/api/village/${village!.id}/rename`, { name }));
    } else if (t.id === "logout") {
      await api("/api/logout", {});
      location.reload();
    }
  } catch (err) {
    toast((err as Error).message);
  }
});

document.addEventListener("submit", async (e) => {
  const form = e.target as HTMLFormElement;
  e.preventDefault();
  if (form.id === "auth-form") return submitAuth("login");
  const fd = new FormData(form);
  if (form.id === "pw-form") {
    try {
      await api("/api/password", { old: fd.get("old"), new: fd.get("new") });
      $<HTMLDialogElement>("#pw-dialog").close();
      toast("Passwort geändert");
    } catch (err) {
      toast((err as Error).message);
    }
    return;
  }
  if (form.id === "admin-settings") {
    const d = await adminAction("settings", {
      announcement: fd.get("announcement"),
      registration: fd.get("registration"),
      registerCode: fd.get("registerCode"),
      worldSpeed: Number(fd.get("worldSpeed")),
      startAt: fd.get("startAt") ? new Date(String(fd.get("startAt"))).getTime() : null,
    });
    if (d) {
      toast("Gespeichert");
      WORLD.speed = d.settings.worldSpeed;
      me!.announcement = d.settings.announcement;
      await loadVillage(village!.id);
      tab = "admin";
      render();
      $("#announcement").hidden = !me!.announcement;
      $("#announcement").textContent = me!.announcement;
    }
    return;
  }
  if (form.id === "admin-reset") {
    if (!confirm("Wirklich die ganze Welt zurücksetzen? Das lässt sich nicht rückgängig machen.")) return;
    const d = await adminAction("reset-world", { keepAccounts: fd.get("keepAccounts") === "on", confirm: fd.get("confirm") });
    if (d) {
      toast("Welt wurde zurückgesetzt");
      village = null;
      await loadVillage(0);
      tab = "admin";
      render();
    }
    return;
  }
  if (form.id === "tribe-create") {
    return tribeAction("create", { name: fd.get("tname"), tag: fd.get("ttag"), color: fd.get("tcolor") });
  }
  if (form.id === "tribe-invite") {
    if (await tribeAction("invite", { name: fd.get("iname") })) toast("Einladung verschickt");
    return;
  }
  if (form.id === "tribe-desc") {
    if (await tribeAction("description", { text: fd.get("tdesc") })) toast("Beschreibung gespeichert");
    return;
  }
  if (form.id === "tribe-post-form") {
    const text = $<HTMLTextAreaElement>("#tribe-post").value;
    $<HTMLTextAreaElement>("#tribe-post").value = "";
    if (!(await tribeAction("post", { text }))) $<HTMLTextAreaElement>("#tribe-post").value = text;
    return;
  }
  try {
    if (form.dataset.recruit) {
      const id = form.dataset.recruit as UnitId;
      const input = form.querySelector<HTMLInputElement>("input")!;
      setVillage(await api<Village>(`/api/village/${village!.id}/recruit`, { unit: id, count: Number(input.value) }));
      $<HTMLInputElement>(`[name=r-${id}]`).value = "";
      toast(`${UNITS[id].name} in Ausbildung`);
    } else if (form.id === "attack-form") {
      const mode = (e as SubmitEvent).submitter?.getAttribute("value") === "support" ? "support" : "attack";
      const body = {
        x: Number($<HTMLInputElement>("[name=tx]").value),
        y: Number($<HTMLInputElement>("[name=ty]").value),
        units: attackUnits(),
      };
      const target = mapCache.get(`${body.x}|${body.y}`);
      if (mode === "attack" && target?.tribeId && target.tribeId === me!.user.tribeId) {
        if (!confirm(`${target.ownerName} ist in deinem Stamm. Trotzdem angreifen?`)) return;
      }
      const v = await api<Village>(`/api/village/${village!.id}/${mode}`, body);
      form.querySelectorAll<HTMLInputElement>("#attack-units input").forEach((i) => (i.value = ""));
      tab = "dorf";
      setVillage(v);
      toast(mode === "support" ? "Unterstützung losgeschickt" : "Angriff losgeschickt");
    }
  } catch (err) {
    toast((err as Error).message);
  }
});

document.addEventListener("change", (e) => {
  const t = e.target as HTMLSelectElement;
  if (t.id === "village-select") {
    mapCenter = null;
    loadVillage(Number(t.value));
  }
});

document.addEventListener("input", (e) => {
  if ((e.target as HTMLElement).closest("#attack-form")) updateAttackPreview();
});

// Sekundentakt: Zahlen live, bei fälligen Ereignissen neu laden
let lastLoad = 0;
setInterval(async () => {
  if (!village || refreshing) return;
  // Alle 15 s neu laden, damit neue eingehende Angriffe, Berichte und Eroberungen sichtbar werden
  const justStarted = !!me?.startAt && serverNow() >= me.startAt;
  if (justStarted) toast("Die Welt ist gestartet – viel Erfolg!");
  if (justStarted || nextChangeAt() <= serverNow() || Date.now() - lastLoad > 15_000) {
    refreshing = true;
    try {
      // kleine Verzögerung, damit der Server-Tick sicher durch ist
      await new Promise((r) => setTimeout(r, 300));
      const keep = tab;
      await loadVillage(village.id);
      tab = keep;
    } finally {
      refreshing = false;
    }
    return;
  }
  renderResources();
  renderCountdown();
  if (tab === "dorf") renderDorf();
  if (tab === "kaserne") renderRecruitQueue();
}, 1000);

start();
